import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, KeyRound, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { type MeResponse, useChangePassword, useMe } from "@/lib/auth";
import { evaluarPassword, passwordValida, REQUISITOS_PASSWORD } from "@/lib/password";
import { AuthShell } from "./AuthShell";
import { PasswordChecklist } from "./PasswordChecklist";
import { PasswordInput } from "./PasswordInput";

// La validación de la política se hace con las reglas nativas de react-hook-form
// (mode "onTouched") apoyadas en lib/password (espejo del backend). No se usa
// zodResolver aquí: la versión de @hookform/resolvers del repo no mapea los errores
// de zod v4 (ver PR/issue #198), y el modelado nativo es reactivo y fiable.
type ChangePasswordFormValues = {
  password_actual: string;
  password_nueva: string;
  password_confirma: string;
};

function aplicarError(
  err: unknown,
  setError: UseFormSetError<ChangePasswordFormValues>,
  setFormError: (msg: string) => void,
  setResaltar: (v: boolean) => void,
): void {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "PASSWORD_ACTUAL_INCORRECTA":
        setError("password_actual", { message: "La contraseña actual no es correcta." });
        return;
      case "PASSWORD_INVALIDA":
        // El backend envía el mensaje exacto de la política en `message`. Resaltamos
        // además la checklist en rojo para que el requisito incumplido quede a la vista.
        setResaltar(true);
        setError("password_nueva", { message: err.message });
        return;
      case "PASSWORD_SIN_CAMBIO":
        setError("password_nueva", {
          message: "La nueva contraseña debe ser distinta de la actual.",
        });
        return;
      case "CAMPOS_REQUERIDOS":
        setFormError("Rellena todos los campos.");
        return;
      default:
        setFormError(err.message || "No se pudo cambiar la contraseña.");
        return;
    }
  }
  setFormError("No se pudo cambiar la contraseña. Comprueba tu conexión.");
}

// Tras el cambio se continúa el onboarding: si queda NDA pendiente, al NDA.
function destinoTrasCambio(me: MeResponse): string {
  return me.nda_pendiente ? "/nda" : "/";
}

// Retardo del redirect tras el éxito: da tiempo a leer el aviso de confirmación.
const REDIRECT_DELAY_MS = 1200;

// Página del cambio de contraseña propio (/mi-clave). Sirve dos casos: el cambio
// VOLUNTARIO, que es el habitual, y el OBLIGATORIO.
//
// El obligatorio (`me.debe_cambiar_password`) es LEGACY-ONLY desde #239 (decisión del
// cliente, #283 D1): ningún flujo activa ya ese flag —el alta invita y la contraseña la
// elige siempre su dueño al canjear el enlace—, así que solo lo verán las cuentas
// anteriores a #239 que quedaran con el flag puesto. La página y el gate del servidor
// (RequirePasswordChanged) se CONSERVAN para ellas.
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const changePassword = useChangePassword();
  const [formError, setFormError] = useState<string | null>(null);
  const [resaltar, setResaltar] = useState(false);
  const [exito, setExito] = useState<string | null>(null);

  const obligatorio = me?.debe_cambiar_password ?? false;

  const {
    register,
    handleSubmit,
    setError,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    mode: "onTouched",
    defaultValues: { password_actual: "", password_nueva: "", password_confirma: "" },
  });

  // La checklist reacciona en vivo a lo que se teclea (bloque 1 de #198).
  const passwordNueva = watch("password_nueva");
  const passwordConfirma = watch("password_confirma");
  const checks = evaluarPassword(passwordNueva);
  const coincide = passwordConfirma.length > 0 && passwordNueva === passwordConfirma;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setResaltar(false);
    try {
      const actualizado = await changePassword.mutateAsync({
        password_actual: values.password_actual,
        password_nueva: values.password_nueva,
      });
      const destino = destinoTrasCambio(actualizado);
      setExito(
        actualizado.nda_pendiente
          ? "A continuación deberás aceptar el acuerdo de confidencialidad."
          : "Te llevamos al inicio.",
      );
      setTimeout(() => {
        void navigate({ to: destino });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      aplicarError(err, setError, setFormError, setResaltar);
    }
  });

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Cambiar contraseña</CardTitle>
          <CardDescription>Elige una nueva contraseña que cumpla los requisitos.</CardDescription>
        </CardHeader>
        <CardContent>
          {exito ? (
            <Alert variant="success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                <strong>Contraseña actualizada.</strong> {exito}
              </span>
            </Alert>
          ) : (
            <>
              {obligatorio ? (
                <Alert variant="info">
                  <KeyRound className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>Debes cambiar tu contraseña antes de continuar.</span>
                </Alert>
              ) : null}

              {formError ? (
                <Alert variant="destructive">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </Alert>
              ) : null}

              <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_actual">Contraseña actual</Label>
                  <PasswordInput
                    id="password_actual"
                    autoComplete="current-password"
                    aria-invalid={errors.password_actual ? true : undefined}
                    {...register("password_actual", {
                      required: "Introduce tu contraseña actual",
                    })}
                  />
                  <FieldError>{errors.password_actual?.message}</FieldError>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_nueva">Nueva contraseña</Label>
                  <PasswordInput
                    id="password_nueva"
                    autoComplete="new-password"
                    aria-invalid={errors.password_nueva ? true : undefined}
                    {...register("password_nueva", {
                      validate: (v) => {
                        if (!passwordValida(v)) return REQUISITOS_PASSWORD;
                        if (v === getValues("password_actual")) {
                          return "La nueva contraseña debe ser distinta de la actual.";
                        }
                        return true;
                      },
                      // Revalida la confirmación cuando cambia la nueva contraseña.
                      onChange: () => {
                        if (getValues("password_confirma")) void trigger("password_confirma");
                      },
                    })}
                  />
                  <FieldError>{errors.password_nueva?.message}</FieldError>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_confirma">Repetir nueva contraseña</Label>
                  <PasswordInput
                    id="password_confirma"
                    autoComplete="new-password"
                    aria-invalid={errors.password_confirma ? true : undefined}
                    {...register("password_confirma", {
                      validate: (v) =>
                        v === getValues("password_nueva") || "Las contraseñas no coinciden",
                    })}
                  />
                  <FieldError>{errors.password_confirma?.message}</FieldError>
                </div>

                <PasswordChecklist checks={checks} coincide={coincide} resaltar={resaltar} />

                <Button
                  type="submit"
                  size="lg"
                  disabled={changePassword.isPending}
                  className="mt-2 w-full"
                >
                  {changePassword.isPending ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <KeyRound className="h-5 w-5" aria-hidden="true" />
                  )}
                  Guardar contraseña
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
